import React, { useEffect, useState } from "react";
import {
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    useTheme,
    MenuItem,
    IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { addEmployee, addProperty, updateEmployee, updateProperty } from "state/apis";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { Close as CloseIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { add } from "dayjs";

const AddPropertyForm = ({ open, onClose, onSubmit, editData, editMode, owners, isInactive }) => {
    const theme = useTheme();
    const [data, setData] = useState({
        empId: "",
        employeeName: "",
        joiningDate: "",
        designation: "",
        salary: "",
        gratuityRecUpTo: "",
        passportExpDate: "",
        emiratesIdExpDate: "",
        leaveSalaryDate: "",
        passportNumber: "",
        emiratesId: "",
        docs: [],
    });

    useEffect(() => {
        if (editMode && editData) {
            setData((prevData) => ({
                ...prevData,
                empId: editData.empId,
                employeeName: editData.employeeName,
                joiningDate: editData.joiningDate,
                designation: editData.designation,
                salary: editData.salary,
                gratuityRecUpTo: editData.gratuityRecUpTo,
                passportExpDate: editData.passportExpDate,
                emiratesIdExpDate: editData.emiratesIdExpDate,
                leaveSalaryDate: editData.leaveSalaryDate,
                passportNumber: editData.passportNumber,
                emiratesId: editData.emiratesId,
            }));
        } else {
            setData({
                empId: "",
                employeeName: "",
                joiningDate: "",
                designation: "",
                salary: "",
                gratuityRecUpTo: "",
                passportExpDate: "",
                emiratesIdExpDate: "",
                leaveSalaryDate: "",
                passportNumber: "",
                emiratesId: "",
                docs: [],
            });
        }
    }, [editData, editMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleDateChange = (date, fieldName) => {
        const formattedDate = date ? date.format("YYYY-MM-DD") : null;
        setData((prevData) => ({
            ...prevData,
            [fieldName]: formattedDate,
        }));
    };

    const handleAddDocument = () => {
        setData((prevData) => ({
            ...prevData,
            docs: [...prevData.docs, { name: "", file: null }],
        }));
    };

    const handleDocumentNameChange = (index, value) => {
        const updatedDocuments = [...data.docs];
        updatedDocuments[index].name = value;
        setData((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleCancelDocument = (indexToRemove) => {
        setData((prevState) => ({
            ...prevState,
            docs: prevState.docs.filter((_, index) => index !== indexToRemove),
        }));
    };
    const handleDocumentFileChange = (index, file) => {
        const updatedDocuments = [...data.docs];
        updatedDocuments[index].file = file;
        setData((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleSubmit = async () => {
        try {
            if (editMode) {
                const response = await updateEmployee(editData.id, data);
                toast.success(response.message);
            } else {
                const response = await addEmployee(data);
                toast.success(response.message);
            }
            onSubmit();
            onClose();
        } catch (error) {
            console.error("Error:", error);
            if (error.response) {
                toast.error(error.response.data.message);
            } else if (error.request) {
                toast.error("No response received from server.");
            } else {
                toast.error("An error occurred. Please try again.");
            }
        }
    };
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    marginLeft: "6px",
                }}
            >
                {isInactive ? "Edit Documents" : editMode ? "Edit Employee" : "Add Employee"}
                <IconButton onClick={onClose} sx={{ color: "inherit" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} p={"6px"}>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Employee Name"
                            name="employeeName"
                            value={data?.employeeName}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Emp Id"
                            name="empId"
                            value={data?.empId}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} color="primary">
                            <DatePicker
                                fullWidth
                                label="Select Joining Date"
                                name="joiningDate"
                                onChange={(date) => handleDateChange(date, "joiningDate")}
                                value={data.joiningDate ? dayjs(data.joiningDate) : null}
                                disabled={isInactive}
                                format="DD/MM/YYYY"
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Salary"
                            name="salary"
                            value={data?.salary}
                            onChange={handleChange}
                            type="number"
                            disabled={isInactive}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} color="primary">
                            <DatePicker
                                fullWidth
                                label="Leave Salary Received Upto"
                                name="leaveSalaryDate"
                                onChange={(date) => handleDateChange(date, "leaveSalaryDate")}
                                value={data.leaveSalaryDate ? dayjs(data.leaveSalaryDate) : null}
                                disabled={isInactive}
                                format="DD/MM/YYYY"
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Designation"
                            name="designation"
                            value={data?.designation}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Gratuity Rec Up To"
                            name="gratuityRecUpTo"
                            value={data?.gratuityRecUpTo}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Passport Number"
                            name="passportNumber"
                            value={data?.passportNumber}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} color="primary">
                            <DatePicker
                                fullWidth
                                label="Select Passport Exp Date"
                                name="passportExpDate"
                                onChange={(date) => handleDateChange(date, "passportExpDate")}
                                value={data.passportExpDate ? dayjs(data.passportExpDate) : null}
                                disabled={isInactive}
                                format="DD/MM/YYYY"
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Emirates Id"
                            name="emiratesId"
                            value={data?.emiratesId}
                            onChange={handleChange}
                            disabled={isInactive}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} color="primary">
                            <DatePicker
                                fullWidth
                                label="Select Emirates Id Exp Date"
                                name="emiratesIdExpDate"
                                onChange={(date) => handleDateChange(date, "emiratesIdExpDate")}
                                disabled={isInactive}
                                value={
                                    data.emiratesIdExpDate ? dayjs(data.emiratesIdExpDate) : null
                                }
                                format="DD/MM/YYYY"
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} m={3} border={1} p={2} sx={{ borderColor: "grey.400" }}>
                        <Grid container alignItems="center">
                            <Grid item xs={8}>
                                <label>Employee Documents</label>
                            </Grid>
                            <Grid item xs={4} container justifyContent="flex-end">
                                <Button variant="contained" onClick={handleAddDocument}>
                                    Add Document
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            {data.docs.map((document, index) => (
                                <Grid container spacing={2} key={index} p={1}>
                                    <Grid item xs={5}>
                                        <TextField
                                            fullWidth
                                            color="primary"
                                            label="Document Name"
                                            value={document.name}
                                            onChange={(e) =>
                                                handleDocumentNameChange(index, e.target.value)
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={5} margin={"auto"}>
                                        <input
                                            type="file"
                                            accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={(e) =>
                                                handleDocumentFileChange(index, e.target.files[0])
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={2} container justify="end" margin="auto">
                                        <IconButton
                                            color="error"
                                            onClick={() => handleCancelDocument(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>

                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            color: theme.palette.primary.light,
                            backgroundColor: theme.palette.secondary[200],
                        }}
                    >
                        {editMode ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

export default AddPropertyForm;
